/**
 * Sharing a view, and taking the sharing back.
 *
 * Two operations on one view: the switch that makes it reachable, and the one
 * that gives it a different address so the old one stops working.
 *
 * **Only the slug is taken from the answer.** Both endpoints serialise the view
 * *without* its filters and sortings -- `RotateViewSlugView` builds a plain
 * `ViewSerializer` with no `filters` in its context, and the `PATCH` does the
 * same -- so writing either answer back would share a view and quietly unfilter
 * it, which the row query's signature would then act on by refetching the whole
 * table.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { View } from '@/api/types'
import { patchView } from './view-cache'

export interface ViewSharing {
  /** Makes the view reachable, or stops it being reachable. */
  setShared: (shared: boolean) => void
  /** Gives it a new address, which is how a link handed out is taken back. */
  rotate: () => void
  isPending: boolean
}

export function useViewSharing(tableId: number, viewId: number): ViewSharing {
  const queryClient = useQueryClient()
  const key = qk.views(tableId)

  // Takes a change rather than a value, because `Partial<View>` over a union
  // of seven view types widens the discriminant and stops being any of them.
  const write = (change: (view: View) => View): void => {
    queryClient.setQueryData<View[]>(key, (views) =>
      views === undefined ? views : patchView(views, viewId, change),
    )
  }

  const share = useMutation({
    mutationFn: (shared: boolean): Promise<unknown> =>
      unwrap(
        getApi().PATCH('/api/database/views/{view_id}/', {
          params: { path: { view_id: viewId } },
          // Cast at the boundary, like every view payload: the body is a union
          // over the seven view types.
          body: { public: shared } as never,
        }),
      ),

    onMutate: (shared) => {
      // Applied at once. The slug is on the view from the moment the view is,
      // so the address is already known and there is nothing to wait for -- and
      // a switch that stays where it was for a round trip reads as one that
      // refused.
      const previous = queryClient.getQueryData<View[]>(key)
      write((view) => ({ ...view, public: shared }))
      return { previous }
    },

    onError: (_error, _shared, context) => {
      queryClient.setQueryData<View[] | undefined>(key, context?.previous)
    },
  })

  const rotate = useMutation({
    mutationFn: (): Promise<View> =>
      unwrap(
        getApi().POST('/api/database/views/{view_id}/rotate-slug/', {
          params: { path: { view_id: viewId } },
        }),
      ),
    // Not optimistic: the new address is the server's to choose, and a client
    // that guessed one would show a link that goes nowhere.
    onSuccess: (rotated) => {
      write((view) => ({ ...view, slug: rotated.slug }))
    },
  })

  return {
    setShared: (shared) => {
      share.mutate(shared)
    },
    rotate: () => {
      rotate.mutate()
    },
    isPending: rotate.isPending,
  }
}
